import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, MessageCircle } from 'lucide-react';
import { trackWhatsapp, trackAction } from '../lib/analytics';

const GEET_LOGO = '/geet-neon.png';
const HERO_INTERIOR =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/i967kzb2_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2010_13_24%20AM.png';

// WhatsApp booking — Gita Jivan
const GEET_WA_LINK = `https://wa.me/27835450004?text=${encodeURIComponent(
  "Hi Gita, I'd like to book a table at Geet Fine Dining.",
)}`;

// Feature images
const IMG_TABLE_SETTING =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/rtsg9i4z_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_26_36%20AM.png';
const IMG_CURRY_POTS =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/9s7m3p8i_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_12_12%20AM.png';
const IMG_WINE_PAIRING =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/81lly2fq_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_09_34%20AM.png';
const IMG_FEAST =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/pfuknvfk_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_18_10%20AM.png';
const IMG_GITA =
  'https://customer-assets.emergentagent.com/job_574cc8bd-edef-41b9-82d3-6a702d274bcf/artifacts/1fs9rvnf_ChatGPT%20Image%20Jun%2015%2C%202026%2C%2011_19_17%20AM.png';

const SECTIONS = [
  {
    eyebrow: 'A Room Dressed for the Occasion',
    title: 'Linen, silver, and golden hour.',
    body:
      'Every table at Geet is set with quiet ceremony — engraved chargers, gold-rimmed glassware, soft taupe linen — so the meal feels like an heirloom even before the first course arrives.',
    image: IMG_TABLE_SETTING,
    align: 'left',
    accent: '#5C4A1F',
    testid: 'geet-section-table',
  },
  {
    eyebrow: 'Curries, Told With Care',
    title: 'Old brass, slow heat, deep flavour.',
    body:
      'Saag paneer, butter chicken, lamb rogan josh — each curry simmers in heirloom brass, finished tableside and paired with a hand-picked South African wine.',
    image: IMG_CURRY_POTS,
    align: 'right',
    accent: '#3F6249',
    testid: 'geet-section-curries',
  },
  {
    eyebrow: 'A Wine List Worth Wandering',
    title: 'Pairings, by candlelight.',
    body:
      'From Meerlust Chardonnay to Stellenbosch reds, our cellar is built to flatter the spices, not fight them. Let our sommelier walk you through a journey from cape to coast.',
    image: IMG_WINE_PAIRING,
    align: 'left',
    accent: '#7B2C2C',
    testid: 'geet-section-wine',
  },
  {
    eyebrow: 'The Feast Unfolds',
    title: 'A table that tells a story.',
    body:
      'Sharing platters arrive in waves — tandoor smoke, saffron rice, freshly milled chutneys — turning dinner into a quiet kind of celebration that lingers long after the plates are cleared.',
    image: IMG_FEAST,
    align: 'right',
    accent: '#B25E2A',
    testid: 'geet-section-feast',
  },
];

const HomePage = () => {
  return (
    <main data-testid="home-page" className="bg-[#FAF6F0] text-[#1C1917]">
      {/* ============ HERO ============ */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_INTERIOR}
            alt="Geet Restaurant dining room at sunset"
            className="w-full h-full object-cover"
          />
          {/* Base overlay — slightly deeper so text reads */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917]/55 via-[#1C1917]/55 to-[#FAF6F0]/50" />
          {/* Radial vignette dim behind the centered logo */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 60% 62% at 50% 42%, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 45%, transparent 78%)',
            }}
          />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-3 mb-7">
            <span className="h-px w-12 bg-[#C9A227]" />
            <span className="font-['Cormorant_Garamond'] italic text-[#FAF6F0] text-sm md:text-base tracking-[0.4em] uppercase drop-shadow">
              Since 2010 · Brooklyn, Pretoria
            </span>
            <span className="h-px w-12 bg-[#C9A227]" />
          </div>

          {/* Logo */}
          <img
            src={GEET_LOGO}
            alt="Geet Fine Dining"
            className="h-28 md:h-44 lg:h-52 w-auto mx-auto mb-7"
          />

          <h1 className="font-['Marcellus'] text-3xl md:text-5xl lg:text-6xl text-[#FAF6F0] mb-5 leading-tight drop-shadow-[0_6px_24px_rgba(0,0,0,0.55)]">
            <span className="italic font-['Cormorant_Garamond'] text-[#FFE7A8]">Classic Indian fine dining.</span>
          </h1>

          {/* Restaurant Week set menu CTA */}
          <Link
            to="/geet/set-menu"
            data-testid="geet-hero-setmenu"
            className="group inline-flex items-center gap-3 mb-8 border border-[#C9A227] bg-[#C9A227]/15 backdrop-blur-sm text-[#FFE7A8] px-6 py-3 font-['Cormorant_Garamond'] tracking-[0.3em] uppercase text-xs md:text-sm rounded-full hover:bg-[#C9A227] hover:text-[#1C1917] transition-all duration-300 shadow-lg"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFE7A8] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FFE7A8]" />
            </span>
            Restaurant Week Set Menu
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>

          <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FAF6F0]/95 max-w-2xl mx-auto mb-10 leading-relaxed drop-shadow">
            Curries, seafood and tandoori reimagined for the modern palate — in a smart, exotic
            space where every guest is treated as royalty.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={GEET_WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              onClick={() => trackWhatsapp('geet')}
              title="WhatsApp Gita Jivan · +27 83 545 0004"
              className="group inline-flex items-center gap-3 bg-[#25D366] text-[#0B3B22] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#1EBE57] transition-all duration-300 shadow-lg"
            >
              <MessageCircle size={18} />
              Book via WhatsApp
            </a>
            <Link
              to="/geet/contact"
              data-testid="hero-contact-btn"
              onClick={() => trackAction('reserve', 'geet')}
              className="group inline-flex items-center gap-3 bg-[#C9A227] text-[#1C1917] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FAF6F0] transition-all duration-300 shadow-lg"
            >
              Reserve a Table
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/geet/menu"
              data-testid="hero-menu-btn"
              className="inline-flex items-center gap-2 border border-[#FAF6F0]/70 text-[#FAF6F0] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm rounded-lg backdrop-blur-sm hover:bg-[#FAF6F0]/10 transition-all duration-300"
            >
              View the Menu
            </Link>
          </div>
          <p
            data-testid="hero-whatsapp-number"
            className="mt-5 font-['Cormorant_Garamond'] italic text-[#FAF6F0]/85 text-sm tracking-wider drop-shadow"
          >
            Prefer to message? WhatsApp Gita on{' '}
            <a
              href={GEET_WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FFE7A8] hover:text-[#C9A227] not-italic tracking-wide"
            >
              +27 83 545 0004
            </a>
          </p>

          {/* Scroll cue */}
          <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[#C9A227]/80">
            <span className="font-['Cormorant_Garamond'] italic tracking-[0.4em] uppercase text-[10px]">
              Scroll
            </span>
            <span className="h-10 w-px bg-gradient-to-b from-[#C9A227]/80 to-transparent" />
          </div>
        </div>
      </section>

      {/* ============ AWARD BANNER ============ */}
      <section
        data-testid="award-banner"
        className="relative bg-[#1C1917] text-[#FAF6F0] py-5 md:py-6 overflow-hidden border-y border-[#C9A227]/30"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-72 h-72 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-5 text-center">
          <span className="font-['Cormorant_Garamond'] italic text-[#C9A227] tracking-[0.4em] uppercase text-xs md:text-sm">
            ✦ Award Winning ✦
          </span>
          <span className="hidden md:block h-5 w-px bg-[#C9A227]/40" />
          <p className="font-['Marcellus'] text-base md:text-xl tracking-wide">
            Voted <span className="text-[#FFE7A8] italic font-['Cormorant_Garamond']">Best Indian Restaurant</span> in South Africa
          </p>
        </div>
      </section>

      {/* ============ CHEF GITA SPOTLIGHT ============ */}
      <section
        data-testid="geet-section-chef"
        className="relative py-24 md:py-32 bg-[#FAF6F0] overflow-hidden"
      >
        <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#C9A227] opacity-10 blur-3xl pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 relative group">
              <div className="relative aspect-[4/5] md:aspect-[4/4] overflow-hidden rounded-2xl shadow-[0_25px_80px_rgba(92,74,31,0.2)]">
                <img
                  src={IMG_GITA}
                  alt="Chef Gita Jivan with her brigade"
                  className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="lg:col-span-6 text-center lg:text-left">
              <div className="flex items-center gap-3 mb-5 lg:justify-start justify-center">
                <span className="h-px w-10 bg-[#C9A227]" />
                <span className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase text-[#C9A227]">
                  Behind the Pass
                </span>
              </div>

              <h2 className="font-['Marcellus'] text-3xl md:text-4xl lg:text-5xl text-[#1C1917] mb-6 leading-tight">
                Chef Gita Jivan &amp;
                <br />
                <span className="italic font-['Cormorant_Garamond'] text-[#5C4A1F]">a family kitchen.</span>
              </h2>

              <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30] leading-relaxed mb-6 max-w-lg mx-auto lg:mx-0">
                For over a decade, Gita has run Geet as a love-letter to her grandmother&apos;s cooking —
                reinterpreting nostalgic Indian dishes with bold sauces, modern technique and a deep
                respect for tradition.
              </p>

              <Link
                to="/geet/about"
                data-testid="about-read-more"
                className="group inline-flex items-center gap-3 text-[#5C4A1F] font-['Cormorant_Garamond'] italic tracking-wider text-lg hover:gap-4 transition-all"
              >
                Read her story
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURE SECTIONS ============ */}
      {SECTIONS.map((s, idx) => (
        <section
          key={s.testid}
          data-testid={s.testid}
          className={`relative py-24 md:py-32 overflow-hidden ${
            idx % 2 === 0 ? 'bg-[#FAF6F0]' : 'bg-[#F3EBDE]'
          }`}
        >
          {/* Soft accent glow */}
          <div
            className="absolute -top-40 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-15 pointer-events-none"
            style={{
              background: s.accent,
              left: s.align === 'left' ? '-12%' : 'auto',
              right: s.align === 'right' ? '-12%' : 'auto',
            }}
          />

          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
            <div
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                s.align === 'right' ? 'lg:[&>*:first-child]:order-2' : ''
              }`}
            >
              {/* Image */}
              <div className="lg:col-span-7 relative group">
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-[0_25px_80px_rgba(92,74,31,0.18)]">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                {/* Decorative number */}
                <div
                  className={`hidden md:block absolute font-['Marcellus'] text-[10rem] leading-none opacity-15 select-none pointer-events-none ${
                    s.align === 'right' ? '-left-6 -top-12' : '-right-6 -top-12'
                  }`}
                  style={{ color: s.accent }}
                >
                  0{idx + 1}
                </div>
              </div>

              {/* Text */}
              <div
                className={`lg:col-span-5 ${
                  s.align === 'right' ? 'lg:text-right' : 'lg:text-left'
                } text-center`}
              >
                <div
                  className={`flex items-center gap-3 mb-5 ${
                    s.align === 'right' ? 'lg:justify-end' : 'lg:justify-start'
                  } justify-center`}
                >
                  <span className="h-px w-10" style={{ background: s.accent }} />
                  <span
                    className="font-['Cormorant_Garamond'] italic text-sm tracking-[0.3em] uppercase"
                    style={{ color: s.accent }}
                  >
                    {s.eyebrow}
                  </span>
                </div>

                <h2 className="font-['Marcellus'] text-3xl md:text-4xl lg:text-5xl text-[#1C1917] mb-6 leading-tight">
                  {s.title}
                </h2>

                <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#3F3A30] leading-relaxed max-w-md mx-auto lg:mx-0">
                  {s.body}
                </p>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ============ CTA FOOTER ============ */}
      <section className="relative py-20 md:py-28 bg-[#1C1917] text-[#FAF6F0] overflow-hidden border-t border-[#C9A227]/20">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#C9A227] opacity-30 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#7B2C2C] opacity-20 blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <p className="font-['Cormorant_Garamond'] italic text-[#C9A227] text-sm tracking-[0.35em] uppercase mb-5">
            ✦  Reservations Recommended  ✦
          </p>
          <h2 className="font-['Marcellus'] text-3xl md:text-5xl mb-6 leading-tight">
            Save your seat at <span className="italic text-[#FFE7A8]">the table.</span>
          </h2>
          <p className="font-['Cormorant_Garamond'] text-lg md:text-xl text-[#FAF6F0]/85 mb-10 inline-flex items-center gap-2 justify-center">
            <MapPin size={18} className="text-[#C9A227]" />
            547 Fehrsen Street, Brooklyn, Pretoria
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={GEET_WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              onClick={() => trackWhatsapp('geet')}
              title="WhatsApp Gita Jivan · +27 83 545 0004"
              className="group inline-flex items-center gap-3 bg-[#25D366] text-[#0B3B22] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#1EBE57] transition-all duration-300 shadow-lg"
            >
              <MessageCircle size={18} />
              Book via WhatsApp
            </a>
            <Link
              to="/geet/contact"
              data-testid="cta-reserve-btn"
              onClick={() => trackAction('reserve', 'geet')}
              className="group inline-flex items-center gap-3 bg-[#C9A227] text-[#1C1917] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm font-semibold rounded-lg hover:bg-[#FAF6F0] transition-all duration-300 shadow-lg"
            >
              Reserve a Table
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/geet/menu"
              className="inline-flex items-center gap-2 border border-[#C9A227]/60 text-[#C9A227] px-8 py-4 font-['Cormorant_Garamond'] tracking-[0.25em] uppercase text-sm rounded-lg hover:bg-[#C9A227]/10 transition-all duration-300"
            >
              View the Menu
            </Link>
          </div>
          <p
            data-testid="cta-whatsapp-number"
            className="mt-6 font-['Cormorant_Garamond'] italic text-[#FAF6F0]/70 text-sm tracking-wider"
          >
            Or WhatsApp Gita Jivan directly on{' '}
            <a
              href={GEET_WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FFE7A8] hover:text-[#C9A227] not-italic tracking-wide"
            >
              +27 83 545 0004
            </a>
          </p>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
